/**
 * Account cancellation workflow (super admin + company admin confirm) — collection ids and statuses.
 * NOT for load/TONU: use CANCELLATION_STATUS in statusValues.ts for loads/POs only.
 * Must match backend/src/config/accountCancellationWorkflow.ts exactly.
 */

export const ACCOUNT_CANCELLATION_COLLECTIONS = {
  CANCELLATION_REQUESTS: 'cancellationRequests',
  ACCOUNT_CANCELLATION_INTENTS: 'accountCancellationIntents',
  TEAM_REMOVAL_CONFIRMATIONS: 'teamRemovalConfirmations',
  SUPER_ADMIN_IN_APP_ALERTS: 'superAdminInAppAlerts',
} as const;

/** Stored on cancellationRequests/{id} */
export const CANCELLATION_REQUEST_STATUS = {
  DRAFT: 'draft',
  PENDING_COMPANY_CONFIRM: 'pending_company_confirm',
  EXECUTED: 'executed',
  EXPIRED: 'expired',
  REVOKED: 'revoked',
} as const;

/** Stored on accountCancellationIntents/{id} (Phase 1b) */
export const ACCOUNT_CANCELLATION_INTENT_STATUS = {
  PENDING_SUPERADMIN_REVIEW: 'pending_superadmin_review',
  LINKED_TO_REQUEST: 'linked_to_request',
  SUPERSEDED: 'superseded',
} as const;

/** Stored on teamRemovalConfirmations/{id} (Phase 7) */
export const TEAM_REMOVAL_CONFIRMATION_STATUS = {
  PENDING: 'pending',
  CONSUMED: 'consumed',
  EXPIRED: 'expired',
} as const;

/** cancellationRequests.source — how the workflow was started */
export const CANCELLATION_REQUEST_SOURCE = {
  SUPER_ADMIN: 'super_admin',
  END_USER_INTENT: 'end_user_intent',
} as const;

/** cancellationRequests.executionResult after Phase 4 executor */
export const CANCELLATION_EXECUTION_RESULT = {
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILED: 'failed',
} as const;

/** Super-admin draft / company-confirm token window (calendar days). Must match backend. */
export const CANCELLATION_REQUEST_DRAFT_EXPIRY_DAYS = 3 as const;

/**
 * Phase 6: rate limits (express-rate-limit on backend). Mirrored on frontend for parity with backend config.
 */
export const ACCOUNT_CANCELLATION_RATE_LIMITS = {
  ADMIN_CREATE_WINDOW_MS: 15 * 60 * 1000,
  ADMIN_CREATE_MAX: 15,
  ADMIN_RESEND_WINDOW_MS: 15 * 60 * 1000,
  ADMIN_RESEND_MAX: 25,
  ADMIN_REVOKE_WINDOW_MS: 15 * 60 * 1000,
  ADMIN_REVOKE_MAX: 30,
  PUBLIC_TOKEN_STATUS_WINDOW_MS: 15 * 60 * 1000,
  PUBLIC_TOKEN_STATUS_MAX: 60,
  PUBLIC_COMPLETE_WINDOW_MS: 15 * 60 * 1000,
  PUBLIC_COMPLETE_MAX: 20,
  /** End-user “request cancellation” intent — per uid + IP */
  END_USER_INTENT_WINDOW_MS: 60 * 60 * 1000,
  END_USER_INTENT_MAX: 10,
} as const;

export const ACCOUNT_CANCELLATION_RATE_LIMIT_CODES = {
  ADMIN_CREATE: 'ADMIN_CANCELLATION_LIMIT',
  ADMIN_RESEND: 'ADMIN_CANCELLATION_RESEND_LIMIT',
  PUBLIC_TOKEN_STATUS: 'CANCELLATION_TOKEN_STATUS_LIMIT',
  PUBLIC_COMPLETE: 'CANCELLATION_COMPLETE_LIMIT',
  END_USER_INTENT: 'REQUEST_CANCELLATION_INTENT_LIMIT',
} as const;

/** Phase 6: concurrent POST /complete — stale claim allows retry after stuck executor. */
export const CANCELLATION_EXECUTION_CLAIM_STALE_MINUTES = 10 as const;

/** Backend securityAuditLog.actionType (Admin SDK); mirrored for parity with backend config. */
export const ACCOUNT_CANCELLATION_AUDIT_ACTIONS = {
  EXECUTION_FAILED: 'account_cancellation_execution_failed',
  EXECUTED: 'account_cancellation_executed',
} as const;

/** Bump only when stored document shape changes (migrate old docs in code if needed). */
export const CANCELLATION_REQUEST_SCHEMA_VERSION = 1 as const;

/** accountCancellationIntents/{id} document schema version */
export const ACCOUNT_CANCELLATION_INTENT_SCHEMA_VERSION = 1 as const;

/**
 * HTTP path segments relative to API base (`getApiUrl()` includes `/api`).
 * Must match backend `auth` router registration.
 */
export const ACCOUNT_CANCELLATION_HTTP = {
  REQUEST_ACCOUNT_CANCELLATION_PATH: '/auth/request-account-cancellation',
} as const;

/** Phase 2: super-admin API (Bearer + step-up password); base path /api/admin */
export const ADMIN_CANCELLATION_HTTP = {
  CREATE_CANCELLATION_REQUEST: '/admin/cancellation-requests',
  RESEND_COMPANY_CONFIRM: '/admin/cancellation-requests/resend-company-confirm',
  REVOKE_PENDING: '/admin/cancellation-requests/revoke-pending',
} as const;

/** Phase 5: in-app super-admin tool (`App.tsx` + `AdminRoute`). Must match backend config for docs/tooling. */
export const ADMIN_CANCELLATION_UI = {
  ROUTE_PATH: '/admin/account-cancellation',
} as const;

/** Phase 3: public token check (no auth); base path includes /api from getApiUrl() */
export const CANCELLATION_CONFIRM_PUBLIC_HTTP = {
  TOKEN_STATUS_PATH: '/cancellation-confirm/token-status',
  /** POST JSON `{ token }` — runs Phase 4 executor (Stripe → Firestore → Auth disable → flags). */
  COMPLETE_PATH: '/cancellation-confirm/complete',
} as const;

/** Phase 3–4: in-app confirmation URL (email link targets main app origin + this path) */
export const CANCELLATION_CONFIRM_UI = {
  ROUTE_PATH: '/confirm-cancellation',
  QUERY_PARAM: 'token',
} as const;

/** Phase 7: team removal — email link to acting admin; must match backend config */
export const TEAM_REMOVAL_TOKEN_TTL_MINUTES = 45 as const;
export const TEAM_REMOVAL_CONFIRM_SCHEMA_VERSION = 1 as const;
export const TEAM_REMOVAL_CLAIM_STALE_MINUTES = 10 as const;

export const TEAM_REMOVAL_HTTP = {
  REQUEST_PATH: '/auth/request-team-removal',
} as const;

export const TEAM_REMOVAL_CONFIRM_PUBLIC_HTTP = {
  TOKEN_STATUS_PATH: '/team-removal-confirm/token-status',
  COMPLETE_PATH: '/team-removal-confirm/complete',
} as const;

export const TEAM_REMOVAL_CONFIRM_UI = {
  ROUTE_PATH: '/confirm-team-removal',
  QUERY_PARAM: 'token',
} as const;

export const TEAM_REMOVAL_RATE_LIMITS = {
  REQUEST_WINDOW_MS: 60 * 60 * 1000,
  REQUEST_MAX: 30,
  PUBLIC_TOKEN_STATUS_WINDOW_MS: 15 * 60 * 1000,
  PUBLIC_TOKEN_STATUS_MAX: 60,
  PUBLIC_COMPLETE_WINDOW_MS: 15 * 60 * 1000,
  PUBLIC_COMPLETE_MAX: 20,
} as const;

export const TEAM_REMOVAL_RATE_LIMIT_CODES = {
  REQUEST: 'TEAM_REMOVAL_REQUEST_LIMIT',
  PUBLIC_TOKEN_STATUS: 'TEAM_REMOVAL_TOKEN_STATUS_LIMIT',
  PUBLIC_COMPLETE: 'TEAM_REMOVAL_COMPLETE_LIMIT',
} as const;

/**
 * Written on `companyUsers/{targetUserId}` and `companyUserLive/{targetUserId}` by Phase 4 executor.
 * Retention: no document delete; flags + Auth disabled per spec §3.
 */
export const ACCOUNT_CANCELLATION_USER_MARKERS = {
  EXECUTED_AT: 'accountCancellationExecutedAt',
  ACCESS_REVOKED: 'accountCancellationAccessRevoked',
  REQUEST_ID: 'accountCancellationRequestId',
} as const;

export type CancellationRequestStatus =
  (typeof CANCELLATION_REQUEST_STATUS)[keyof typeof CANCELLATION_REQUEST_STATUS];
export type AccountCancellationIntentStatus =
  (typeof ACCOUNT_CANCELLATION_INTENT_STATUS)[keyof typeof ACCOUNT_CANCELLATION_INTENT_STATUS];
export type TeamRemovalConfirmationStatus =
  (typeof TEAM_REMOVAL_CONFIRMATION_STATUS)[keyof typeof TEAM_REMOVAL_CONFIRMATION_STATUS];
export type CancellationRequestSource =
  (typeof CANCELLATION_REQUEST_SOURCE)[keyof typeof CANCELLATION_REQUEST_SOURCE];
export type CancellationExecutionResult =
  (typeof CANCELLATION_EXECUTION_RESULT)[keyof typeof CANCELLATION_EXECUTION_RESULT];
